import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input } from '@design-system';
import { useMonitorAvailability } from '@/features/illustration-monitor/hooks/use-illustration-monitor';
import { AvailabilityRow } from '@/features/illustration-monitor/types';

export function IllustrationMonitorAvailabilityPage() {
  const { data, isLoading, isError, refetch, isFetching } = useMonitorAvailability();
  const [filter, setFilter] = useState('');

  const { states, productRows } = useMemo(() => buildMatrix(data?.rows ?? [], filter), [data, filter]);

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden">
      <header className="flex items-start justify-between gap-4 border-b border-gray-200 p-6">
        <div>
          <h1 className="text-2xl font-bold text-darkPurple">Product × State Availability</h1>
          <p className="text-sm text-gray-600">
            All carrier/product/state combinations that have at least one quote in the database.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/wizard/illustration-finder"
            className="text-sm font-medium text-primary underline"
          >
            ← Back to finder
          </Link>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </header>

      <div className="border-b border-gray-200 px-6 py-3">
        <Input
          placeholder="Filter by carrier or product..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <main className="flex-1 overflow-auto p-6">
        {isLoading && <div className="p-8 text-center text-sm text-gray-500">Loading...</div>}
        {isError && (
          <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700">
            Failed to load availability.
          </div>
        )}
        {!isLoading && productRows.length === 0 && (
          <div className="p-8 text-center text-sm text-gray-500">No products match.</div>
        )}

        {productRows.length > 0 && (
          <div className="overflow-auto">
            <table className="border-separate border-spacing-0 text-xs">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  <th className="sticky left-0 z-20 border-b border-r border-gray-200 bg-white px-3 py-2 text-left font-semibold">
                    Carrier
                  </th>
                  <th className="sticky left-[12rem] z-20 border-b border-r border-gray-200 bg-white px-3 py-2 text-left font-semibold">
                    Product
                  </th>
                  {states.map(state => (
                    <th
                      key={state}
                      className="border-b border-gray-200 px-2 py-2 text-center font-semibold"
                    >
                      {state}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {productRows.map(row => (
                  <tr key={`${row.carrierName}::${row.productName}`} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 border-b border-r border-gray-200 bg-white px-3 py-1.5 text-gray-700">
                      {row.carrierName}
                    </td>
                    <td className="sticky left-[12rem] z-10 border-b border-r border-gray-200 bg-white px-3 py-1.5 font-medium text-darkPurple">
                      {row.productName}
                    </td>
                    {states.map(state => {
                      const ratedState = row.byState[state];
                      return (
                        <td
                          key={state}
                          className={`border-b border-gray-200 px-2 py-1.5 text-center ${
                            ratedState
                              ? 'bg-primary-50 text-green-700'
                              : 'text-gray-200'
                          }`}
                          title={
                            ratedState
                              ? `Rated state: ${ratedState}. Carriers group states with identical pricing into a single "rated state." All quotes for ${state} use ${ratedState}'s rate table.`
                              : 'Not available in this state'
                          }
                        >
                          {ratedState ? '✓' : '·'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 text-xs text-gray-500">
              {productRows.length} product rows · {states.length} states
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

type ProductRow = {
  carrierName: string;
  productName: string;
  byState: Record<string, string>;
};

function buildMatrix(rows: AvailabilityRow[], filterRaw: string) {
  const filter = filterRaw.trim().toLowerCase();
  const stateSet = new Set<string>();
  const byKey = new Map<string, ProductRow>();

  for (const r of rows) {
    stateSet.add(r.clientState);
    if (
      filter &&
      !r.carrierName.toLowerCase().includes(filter) &&
      !r.productName.toLowerCase().includes(filter)
    ) {
      continue;
    }
    const key = `${r.carrierName}::${r.productName}`;
    let entry = byKey.get(key);
    if (!entry) {
      entry = { carrierName: r.carrierName, productName: r.productName, byState: {} };
      byKey.set(key, entry);
    }
    entry.byState[r.clientState] = r.ratedState;
  }

  const states = Array.from(stateSet).sort();
  const productRows = Array.from(byKey.values()).sort((a, b) =>
    a.carrierName === b.carrierName
      ? a.productName.localeCompare(b.productName)
      : a.carrierName.localeCompare(b.carrierName),
  );

  return { states, productRows };
}
